import type { Organization, UserDataAndState } from "@glide/common-core/dist/js/Database";
import { isOrganizationAdmin, isUserData } from "@glide/common-core/dist/js/Database";
import type { OrganizationFolder } from "@glide/common-core/dist/js/firebase-function-types";
import { canUserAccessFolder } from "@glide/common-core/dist/js/firebase-function-types";
import { useMemo } from "react";
import { useBuilderSelector } from "./use-builder-selector";

type PermissionLevel = "admin" | "member";

export interface FolderPermissionsContext {
    /**
     * Whether any folder in the organization has fine-grained permissions enabled.
     * True if ANY folder has permissions !== true (meaning specific user permissions are set).
     */
    readonly areFolderPermissionsEnabled: boolean;

    /**
     * Whether the current user is an admin of the organization.
     */
    readonly isAdmin: boolean;

    /**
     * Whether the current user can transfer apps between organizations.
     * Already computed based on permission mode and user's admin status.
     */
    readonly canTransferApp: boolean;

    /**
     * Whether the current user can move apps to different folders.
     * Already computed based on permission mode and user's admin status.
     */
    readonly canMoveToFolder: boolean;

    /**
     * Whether the current user can add existing tables from other apps.
     * Already computed based on permission mode and user's admin status.
     */
    readonly canAddExistingTableToApp: boolean;

    /**
     * Whether the current user can get/generate API keys.
     * Already computed based on permission mode and user's admin status.
     */
    readonly canGetAPIKey: boolean;

    /**
     * Whether the current user can delete tables or export data.
     * Already computed based on permission mode and user's admin status.
     */
    readonly canDeleteTableOrExportData: boolean;
}

/**
 * Hook to determine folder permissions context for an organization.
 *
 * Once folder permissions are enabled (any folder has fine-grained permissions),
 * certain operations must be restricted to admins to prevent data leaks.
 * For example, a user could add a table from a restricted folder to bypass permissions.
 *
 * @param orgID The organization ID to check permissions for
 * @returns Folder permissions context with access control flags
 */
export function useFolderPermissionsContext(orgID: string | undefined): FolderPermissionsContext {
    const { organizations, userData } = useBuilderSelector(state => ({
        organizations: state.organizations,
        userData: state.userData,
    }));

    return useMemo(() => {
        // Find the organization
        const org: Organization | undefined = organizations.find(o => o.id === orgID);

        // Check if folder permissions are enabled
        // Permissions are enabled if ANY folder has permissions !== true
        const areFolderPermissionsEnabled = org?.folders?.some(folder => folder.permissions !== true) ?? false;

        // Check if current user is an admin
        const isAdmin = checkIsAdmin(org, userData);

        // When folder permissions are enabled, restrict sensitive operations to admins
        // Otherwise, allow all members to perform these operations
        const permissionLevel: PermissionLevel = areFolderPermissionsEnabled ? "admin" : "member";

        // Compute boolean values: true if permission is "member" OR user is admin
        const canTransferApp = permissionLevel === "member" || isAdmin;
        const canMoveToFolder = permissionLevel === "member" || isAdmin;
        const canAddExistingTableToApp = permissionLevel === "member" || isAdmin;
        const canGetAPIKey = permissionLevel === "member" || isAdmin;
        const canDeleteTableOrExportData = permissionLevel === "member" || isAdmin;

        return {
            areFolderPermissionsEnabled,
            isAdmin,
            canTransferApp,
            canMoveToFolder,
            canAddExistingTableToApp,
            canGetAPIKey,
            canDeleteTableOrExportData,
        };
    }, [organizations, userData, orgID]);
}

/**
 * Helper function to check if the current user is an admin of the organization.
 */
function checkIsAdmin(org: Organization | undefined, userData: UserDataAndState): boolean {
    if (org === undefined || !isUserData(userData)) {
        return false;
    }
    return isOrganizationAdmin(org, userData.id);
}

export interface AppFolderPermission {
    /**
     * Whether the current user can read the app.
     * False if the user has no access to the folder containing the app.
     */
    readonly canRead: boolean;

    /**
     * Whether the current user can write/edit the app.
     * False if the user only has reader permissions on the folder.
     */
    readonly canWrite: boolean;
}

export function canUserAccessMaybeFolder(
    folderID: string | undefined,
    folders: readonly OrganizationFolder[] | undefined,
    currentUserID: string | undefined
): boolean {
    if (folderID === undefined) {
        return true;
    }

    // Fail closed until folders are loaded so restricted apps never flash.
    if (folders === undefined) {
        return false;
    }

    const folder = folders.find(f => f.id === folderID);
    if (folder === undefined) {
        return false;
    }

    return canUserAccessFolder(folder.permissions, currentUserID);
}

/**
 * Hook to determine the current user's permission level for a specific app.
 *
 * Checks folder permissions to determine if the user can read and/or write to an app.
 * - If the app has no folder or the folder has no fine-grained permissions, all org members can write.
 * - If the user is in writerUserIDs, they can read and write.
 * - If the user is in readerUserIDs, they can only read.
 * - If the user is not in either list, they have no access.
 * - Admins always have write access.
 *
 * @param appID The app ID to check permissions for
 * @param orgID The organization ID that owns the app
 * @returns Permission object with canRead and canWrite flags
 */
export function useAppFolderPermission(appID: string | undefined, orgID: string | undefined): AppFolderPermission {
    const { organizations, userData } = useBuilderSelector(state => ({
        organizations: state.organizations,
        userData: state.userData,
    }));

    return useMemo(() => {
        // Default: no access
        if (appID === undefined || orgID === undefined || !isUserData(userData)) {
            return { canRead: false, canWrite: false };
        }

        // Find the organization
        const org: Organization | undefined = organizations.find(o => o.id === orgID);
        if (org === undefined) {
            return { canRead: false, canWrite: false };
        }

        // Check if user is an admin (admins always have full access)
        const isAdmin = isOrganizationAdmin(org, userData.id);
        if (isAdmin) {
            return { canRead: true, canWrite: true };
        }

        // Find the folder containing this app
        const folder = org.folders?.find(f => f.appIDs.includes(appID));

        // If app is not in any folder, or folder has no fine-grained permissions,
        // all org members have write access
        if (folder === undefined || folder.permissions === true) {
            return { canRead: true, canWrite: true };
        }

        // If permissions is false, user has no access
        if (folder.permissions === false) {
            return { canRead: false, canWrite: false };
        }

        // Check fine-grained permissions
        const userID = userData.id;
        const isWriter = folder.permissions.writerUserIDs.includes(userID);
        const isReader = folder.permissions.readerUserIDs.includes(userID);

        if (isWriter) {
            return { canRead: true, canWrite: true };
        }
        if (isReader) {
            return { canRead: true, canWrite: false };
        }

        // User not in any permission list
        return { canRead: false, canWrite: false };
    }, [appID, orgID, organizations, userData]);
}
